import { useMemo } from 'react'

import { Size } from '@syconium/little-miss-figgy'

import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useSiteWideMessaging } from '../../../../../../app/_providers/SiteWideMessagingProvider.client'
import { deriveFitNamesMap, isColorFitComboValid } from '../../../../../../lib/utils'
import { Variant } from '../../../../../../types/graphql'
import { ProductPageProduct } from '../../hooks/types/ProductPageProduct'
import { ProductPageProductGroup } from '../../hooks/types/ProductPageProductGroup'
import { getDiscountPrice, getPrice } from '../../hooks/types/utils'
import { useProductPagePortal } from '../../hooks/useProductPagePortal'

export type UseProductDetailsOpts = {
  featuredProduct: ProductPageProduct
  productGroup: ProductPageProductGroup
  selectedVariant?: Variant
}

export type MakeUpdateQueryParam = <S>(
  key: string,
  extractValue: (selection: S) => string
) => (selection: S) => void

export const useProductDetails = (input: UseProductDetailsOpts) => {
  // Get the current featured product/product group/selected variant from input
  const { featuredProduct, productGroup, selectedVariant } = input

  const { portal } = usePortal()

  // string fixtures related to the product details section
  const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()

  // The products that are available for the current product group
  const availableProducts = useMemo(
    () => ProductPageProductGroup.getAvailableProducts(productGroup.productsForAvailability),
    [productGroup]
  )

  // The product category for the currently active featured product
  const category = useMemo(
    () => selectedVariant?.category ?? (featuredProduct?.variants ?? [])[0]?.category,
    [featuredProduct?.variants, selectedVariant?.category]
  )

  const collectionLinks = useMemo(
    () => featuredProduct?.collectionLinks.nodes ?? [],
    [featuredProduct?.collectionLinks.nodes]
  )

  const portalColorGroups = useMemo(
    () =>
      ProductPageProductGroup.getColorGroupsWithUnavailable(
        productGroup?.portalColorGroups ?? [],
        productGroup.productsForAvailability,
        featuredProduct?.rawFit,
        selectedVariant?.size
      ),
    [featuredProduct?.rawFit, productGroup, selectedVariant?.size]
  )

  const colorGroups = useMemo(() => {
    const groups = ProductPageProductGroup.getColorGroupsWithUnavailable(
      productGroup?.colorGroups ?? [],
      productGroup.productsForAvailability,
      featuredProduct?.rawFit,
      selectedVariant?.size
    )

    if (!portalColorGroups || portalColorGroups.length === 0) return groups

    // Exclude the colors/color groups that are present in the portal color groups response
    return ProductPageProductGroup.excludeMatchingColors(groups, portalColorGroups)
  }, [featuredProduct?.rawFit, productGroup, selectedVariant?.size, portalColorGroups])

  const colorPriceRanges = useMemo(() => {
    return ProductPageProductGroup.getColorPriceRanges(productGroup.productsForAvailability)
  }, [productGroup.productsForAvailability])

  const { discountPrice, discountPriceExplicitFmt } = useMemo(
    () => getDiscountPrice({ selectedVariant, featuredProduct }),
    [featuredProduct, selectedVariant]
  )

  const fitNames = useMemo(() => deriveFitNamesMap(productGroup), [productGroup])

  const fitKeys = useMemo(() => {
    return productGroup?.rawFits.filter(fitKey =>
      isColorFitComboValid({
        availableProducts,
        colorKey: ProductPageProduct.getSelectedColorKey(featuredProduct) ?? '',
        fitKey,
      })
    )
  }, [availableProducts, featuredProduct, productGroup?.rawFits])

  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups: productGroup.portalColorGroups ?? [],
    selectedColorKey: ProductPageProduct.getSelectedColorKey(featuredProduct),
  })

  const { price, priceExplicitFmt } = useMemo(
    () => getPrice({ selectedVariant, featuredProduct }),
    [featuredProduct, selectedVariant]
  )

  const sizes = useMemo(
    () => ProductPageProduct.getSizes(featuredProduct, productGroup.sizes),
    [featuredProduct, productGroup.sizes]
  )

  const selectedSize: Size | undefined = useMemo(() => {
    return sizes.find(size => size.shorthand === selectedVariant?.size)
  }, [selectedVariant, sizes])

  const selectedColorKey = useMemo(
    () => ProductPageProduct.getSelectedColorKey(featuredProduct),
    [featuredProduct]
  )

  // Get the promotional text to display in the details section.
  // Returns "undefined" if no promotional text should be rendered.
  const promoText = useMemo(() => {
    // If a portal color is selected & the portal has an active discount percentage, don't display sitewide promo text
    if (isPortalColorSelected && portal?.discountPercentage) return undefined
    // If the product is site-wide promo eligible, display the site-wide promo text
    if (featuredProduct.swPromoEligible && siteWidePromo) return siteWidePromo
    return undefined
  }, [portal, isPortalColorSelected, featuredProduct.swPromoEligible, siteWidePromo])

  const wwltImage = useMemo(
    () => featuredProduct.images?.nodes.find(image => image.kind === 'wwlt'),
    [featuredProduct.images?.nodes]
  )

  const isProductFinalSale = ProductPageProduct.isProductFinalSale(featuredProduct)

  const isProductEmbroiderable = ProductPageProduct.isProductEmbroiderable(featuredProduct)

  const isProductOnWaitlist =
    featuredProduct.waitlistable && (selectedSize?.soldOut || featuredProduct.soldOut)

  return {
    availableProducts,
    category,
    collectionLinks,
    colorGroups,
    colorPriceRanges,
    portalColorGroups,
    discountPrice,
    discountPriceExplicitFmt,
    featuredProduct,
    fitKeys,
    fitNames,
    isPortalColorSelected,
    isProductEmbroiderable,
    isProductFinalSale,
    isProductOnWaitlist,
    price,
    priceExplicitFmt,
    productGroup,
    promoText,
    selectedColorKey,
    selectedSize,
    selectedVariant,
    sizes,
    wwltImage,
  }
}
